import { createApi } from '@reduxjs/toolkit/query/react';
import { IApiResponse, } from '../../interfaces/common';
import { baseQuery } from './common';
import { ICreateSubscription, ISubscriptionOrderHistoryItem, ISubscriptionOrderPayload, ISubscriptionPlanMain } from '../../interfaces/subscription';

export const subcriptionApi = createApi({
	reducerPath: 'subscription',
	baseQuery: baseQuery,
	tagTypes: ['Subscription'],
	refetchOnReconnect: true,
	endpoints: (builder) => ({
		getSubscriptionPlanMain: builder.query<IApiResponse<ISubscriptionPlanMain[]>, void>({
			query: () => `/subscription/plan/main`,
			providesTags: [{ type: 'Subscription', id: 'LIST' }],
		}),
		getSubscriptionPlanMini: builder.query<IApiResponse<ISubscriptionPlanMain[]>, void>({
			query: () => `/subscription/plan/mini`,
			providesTags: [{ type: 'Subscription', id: 'LIST' }],
		}),
		validateSubscriptionUserSlot: builder.mutation<IApiResponse<{ success?: boolean, message?: string }>, { data: any }>({
			query: (data) => ({
				url: `/subscription/validate/user-slot`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Subscription'],
		}),
		validateSubscriptionCreatePrice: builder.mutation<IApiResponse<{ success?: boolean, message?: string }>, { data: any }>({
			query: (data) => ({
				url: `/subscription/validate/create-price`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Subscription'],
		}),
		validateSubscriptionPromo: builder.mutation<IApiResponse<{ success?: boolean, message?: string }>, { data: any }>({
			query: (data) => ({
				url: `/subscription/validate/promo`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Subscription'],
		}),
		createSubscription: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		payForSubscription: builder.mutation<any, { id: string }>({
			query: ({ id }) => ({
				url: `/subscription/order/${id}/pay`,
				method: 'POST',
			}),
			invalidatesTags: ['Subscription'],
		}),
		renewSubscription: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription/renew`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		upgradeSubscription: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription/upgrade`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		changeSubscription: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription/change`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		addMiniPlan: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription/mini-plan`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		addUserSlot: builder.mutation<ICreateSubscription, Partial<ICreateSubscription>>({
			query: (body) => ({
				url: `/subscription/user-slot`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
		getUsersSlot: builder.query<IApiResponse<{ active: number, inactive: number, disabled: number }>, { hospitalId: string }>({
			query: ({ hospitalId }) => `/users/count/${hospitalId}`,
			providesTags: (result, error, arg) => [{ type: 'Subscription', id: 'LIST' }],
		}),
		getSubscription: builder.query<IApiResponse<ISubscriptionOrderPayload>, void>({
			query: () => `/subscription`,
			providesTags: (result, error, arg) => [{ type: 'Subscription', id: 'LIST' }],
		}),
		getSubscriptionOrder: builder.query<IApiResponse<ISubscriptionOrderHistoryItem[]>, void>({
			query: () => `/subscription/order`,
			providesTags: (result, error, arg) => [{ type: 'Subscription', id: 'LIST' }],
		}),
		getSubscriptionInvoice: builder.query<IApiResponse<ISubscriptionOrderHistoryItem[]>, void>({
			query: () => `/subscription/invoice`,
			providesTags: (result, error, arg) => [{ type: 'Subscription', id: 'LIST' }],
		}),
		getSubscriptionInvoiceId: builder.query<IApiResponse<ISubscriptionOrderHistoryItem>, { id }>({
			query: ({ id }) => `/subscription/invoice/${id}`,
			providesTags: (result, error, arg) => [{ type: 'Subscription', id: 'LIST' }],
		}),
		cancelSubsription: builder.mutation<IApiResponse<{ success?: boolean, message?: string }>, { reason: string }>({
			query: (body) => ({
				url: `/subscription`,
				method: 'DELETE',
				body,
			}),
			invalidatesTags: ['Subscription'],
		}),
	}),
});

export const {
	useGetSubscriptionPlanMainQuery,
	useGetSubscriptionPlanMiniQuery,
	useValidateSubscriptionUserSlotMutation,
	useValidateSubscriptionCreatePriceMutation,
	useValidateSubscriptionPromoMutation,
	useCreateSubscriptionMutation,
	usePayForSubscriptionMutation,
	useRenewSubscriptionMutation,
	useUpgradeSubscriptionMutation,
	useChangeSubscriptionMutation,
	useAddMiniPlanMutation,
	useAddUserSlotMutation,
	useGetUsersSlotQuery,
	useGetSubscriptionQuery,
	useGetSubscriptionOrderQuery,
	useGetSubscriptionInvoiceQuery,
	useGetSubscriptionInvoiceIdQuery,
	useCancelSubsriptionMutation,
} = subcriptionApi;
